import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import InlineBanner, {
  InlineBannerActionProps,
  InlineBannerActionType,
  InlineBannerProps,
} from 'ui-library/banners/InlineBanner';

export interface InlineBannerStackProps {
  /** A list of InlineBannerProps. Will be rendered in given order. */
  messages: Array<InlineBannerProps>;
}

/**
 * A stack of banners, styled for varying meanings (Error, Warning, etc.).
 *
 * Note:
 *  If the action prop is provided for a banner, rightAlign will be overridden to true.
 *
 * @param props Component props
 *
 * @storybook https://dev--622a4c57262ed1003a5a57fa.chromatic.com/?path=/story/ui-library-banners-inlinebannerstack--basic
 */
export default function InlineBannerStack(props: InlineBannerStackProps): JSX.Element {
  return (
    <div className={css(styles.stack)} role="group">
      {props.messages.map((bannerProps, index) => {
        let action: InlineBannerActionProps | undefined;
        // Force rightAlign* to true if action is provided.
        if (bannerProps.action != null) {
          if (bannerProps.action.actionType === InlineBannerActionType.ACCORDION_AND_CUSTOM) {
            action = {...bannerProps.action, rightAlignActionButton: false, rightAlignAccordionButton: true};
          } else {
            action = {...bannerProps.action, rightAlign: true};
          }
        }
        return (
          <InlineBanner
            key={index}
            {...bannerProps}
            dataTestAttrs={{
              ...(bannerProps.dataTestAttrs ?? {
                // Default dataTestAttrs, if not provided.
                component: 'InlineBanner',
                element: 'root',
              }),
              key: `${index}`,
            }}
            action={action}
          />
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  stack: {
    display: 'grid',
    flexDirection: 'column',
    rowGap: 8,
  },
});
